import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, Table as ReactTable, Row } from "@tanstack/react-table";
import { Table, TBody, TCell, THead, THeadRow, TRow } from "@theprelude/ds";
import React, { CSSProperties } from "react";
import { VirtualSortedHeader } from "./header";
import { getGridTemplateColumns, useHeaderWidths } from "./hooks";
import { LoadingTable } from "./loading-table";
import { TableOptions } from "./table-options";

export const RowDragHandleCell = ({ rowId }: { rowId: string }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <button
      {...attributes}
      type="button"
      {...listeners}
      className="cursor-grab"
    >
      🟰
    </button>
  );
};

// Row Component

export function DndTable<TData>({
  className,
  table,
  children,
  loading,
  emptyState,
  onDragEnd,
}: {
  loading?: boolean;
  className?: string;
  table: ReactTable<TData>;
  showFilter?: boolean;
  children?: React.ReactNode;
  emptyState?: React.ReactNode;
  onDragEnd: (data: string[]) => void;
}) {
  const dataIds = table.getRowModel().rows.map((row) => row.id);
  const { applyAllHeaderWidths, registerHeaderRef } = useHeaderWidths(table);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id.toString());
      const newIndex = dataIds.indexOf(over.id.toString());
      const newData = arrayMove(dataIds, oldIndex, newIndex);
      onDragEnd(newData);
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const gridTemplateColumns = getGridTemplateColumns(table);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <Table className={className}>
        <TableOptions table={table} />
        <THead>
          {table.getHeaderGroups().map((headerGroup) => (
            <THeadRow
              key={headerGroup.id}
              style={{
                gridTemplateColumns,
              }}
            >
              {headerGroup.headers.map((header) => (
                <VirtualSortedHeader
                  key={header.id}
                  header={header}
                  onResize={applyAllHeaderWidths}
                  ref={(el) => registerHeaderRef(header.id, el)}
                />
              ))}
            </THeadRow>
          ))}
        </THead>
        <DndTableBody table={table} loading={loading} emptyState={emptyState} />
        {children}
      </Table>
    </DndContext>
  );
}

const DraggableRow = ({
  row,
  gridTemplateColumns,
}: {
  gridTemplateColumns: string;
  row: Row<any>;
}) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };
  return (
    <TRow
      ref={setNodeRef}
      style={{
        ...style,
        gridTemplateColumns,
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <TCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TCell>
        );
      })}
    </TRow>
  );
};

function DndTableBody<TData>({
  table,
  loading,
  emptyState,
}: {
  table: ReactTable<TData>;
  loading?: boolean;
  emptyState?: React.ReactNode;
}) {
  const { rows } = table.getRowModel();
  const gridTemplateColumns = getGridTemplateColumns(table);
  const dataIds = table.getRowModel().rows.map((row) => row.id);

  if (loading) {
    return <LoadingTable table={table} rowCount={5} />;
  }

  if (rows.length === 0 && emptyState) {
    return (
      <TBody>
        <TRow>
          <TCell>{emptyState}</TCell>
        </TRow>
      </TBody>
    );
  }

  return (
    <TBody>
      <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
        {rows.map((row) => {
          return (
            <DraggableRow
              key={row.id}
              row={row}
              gridTemplateColumns={gridTemplateColumns}
            />
          );
        })}
      </SortableContext>
    </TBody>
  );
}
